import { getEnv } from '../utils/env';

const keycloakUrl = getEnv('REACT_APP_KEYCLOAK_URL', 'https://auth.hazcheck.com/auth');
const realm = getEnv('REACT_APP_KEYCLOAK_REALM', 'chatbot');
const clientId = getEnv('REACT_APP_KEYCLOAK_CLIENT_ID', 'chat-bot');

console.log('🔐 Keycloak Configuration Loaded:');
console.log('  URL:', keycloakUrl);
console.log('  Realm:', realm);
console.log('  Client ID:', clientId);

class KeycloakAdapter {
  authServerUrl: string;
  realm: string;
  clientId: string;
  authenticated: boolean = false;
  token: string | null = null;
  tokenParsed: any = null;
  refreshToken: string | null = null;
  idToken: string | null = null;
  profile: any = null;
  responseMode: string = 'fragment';
  flow: string = 'standard';
  onAuthSuccess?: () => void;
  onAuthError?: (error: any) => void;
  onTokenExpired?: () => void;

  constructor(config: { url: string; realm: string; clientId: string }) {
    this.authServerUrl = config.url;
    this.realm = config.realm;
    this.clientId = config.clientId;
  }

  async init(options: any = {}) {
    console.log('🚀 Initializing Keycloak adapter...');
    
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const urlParams = new URLSearchParams(window.location.search);
    
    const code = params.get('code') || urlParams.get('code');
    const error = params.get('error') || urlParams.get('error');
    
    if (error) {
      console.error('❌ Authentication error:', error);
      const errorDescription = params.get('error_description') || urlParams.get('error_description');
      if (errorDescription) {
        console.error('Error description:', errorDescription);
      }
      return false;
    }
    
    if (code) {
      console.log('🔑 Authorization code found, exchanging via backend...');
      
      try {
        const apiUrl = getEnv('REACT_APP_API_URL', '');
        const redirectUri = window.location.origin + window.location.pathname;
        
        const response = await fetch(`${apiUrl}/api/v1/auth/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            redirect_uri: redirectUri,
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          this.processTokenResponse(data);
          
          window.history.replaceState({}, document.title, window.location.pathname);
          
          console.log('✅ Authentication successful!');
          return true;
        } else {
          const errorText = await response.text();
          console.error('❌ Token exchange failed:', errorText);
          
          console.log('Retrying login...');
          this.login();
          return false;
        }
      } catch (error) {
        console.error('❌ Token exchange error:', error);
        console.log('Retrying login...');
        this.login();
        return false;
      }
    }
    
    const storedToken = sessionStorage.getItem('keycloak_token');
    if (storedToken) {
      try {
        const tokenData = JSON.parse(storedToken);
        if (this.isTokenValid(tokenData)) {
          this.processTokenResponse(tokenData);
          console.log('✅ Restored session from storage');
          return true;
        }
      } catch (error) {
        console.log('Session restoration failed:', error);
        sessionStorage.removeItem('keycloak_token');
      }
    }
    
    if (options.onLoad === 'check-sso') {
      console.log('No active session found');
      return false;
    }
    
    if (options.onLoad === 'login-required') {
      console.log('Login required, redirecting...');
      this.login();
      return false;
    }
    
    return false;
  }

  processTokenResponse(data: any) {
    this.token = data.access_token;
    this.refreshToken = data.refresh_token;
    this.idToken = data.id_token;
    this.authenticated = true;
    
    if (this.token) {
      try {
        const parts = this.token.split('.');
        if (parts.length === 3) {
          this.tokenParsed = JSON.parse(atob(parts[1]));
        }
      } catch (error) {
        console.error('Failed to parse token:', error);
      }
    }
    
    sessionStorage.setItem('keycloak_token', JSON.stringify({
      access_token: this.token,
      refresh_token: this.refreshToken,
      id_token: this.idToken,
      expires_at: Date.now() + (data.expires_in ? data.expires_in * 1000 : 3600000),
    }));
  }

  isTokenValid(tokenData: any): boolean {
    if (!tokenData.access_token) return false;
    if (!tokenData.expires_at) return false;
    return Date.now() < tokenData.expires_at - 30000;
  }

  login(options: any = {}) {
    const redirectUri = options.redirectUri || window.location.origin + window.location.pathname;
    const responseMode = options.responseMode || 'fragment';
    const responseType = options.responseType || 'code';
    const nonce = this.generateNonce();
    
    const loginUrl = `${this.authServerUrl}/realms/${this.realm}/protocol/openid-connect/auth?` +
      `client_id=${this.clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=${responseType}&` +
      `scope=openid%20profile%20email&` +
      `response_mode=${responseMode}&` +
      `nonce=${nonce}`;
    
    console.log('🔐 Redirecting to Keycloak (authorization code flow):', loginUrl);
    window.location.href = loginUrl;
  }
  
  generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  logout(options: any = {}) {
    const redirectUri = options.redirectUri || window.location.origin;
    
    const logoutUrl = `${this.authServerUrl}/realms/${this.realm}/protocol/openid-connect/logout?` +
      `post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    sessionStorage.removeItem('keycloak_token');
    
    console.log('👋 Logging out:', logoutUrl);
    window.location.href = logoutUrl;
  }

  async updateToken(minValidity: number = 5): Promise<boolean> {
    if (!this.refreshToken) {
      console.log('No refresh token available');
      return false;
    }
    
    try {
      const apiUrl = getEnv('REACT_APP_API_URL', 'http://localhost:8000');
      
      const response = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        this.processTokenResponse(data);
        console.log('🔄 Token refreshed successfully');
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    
    return false;
  }

  async loadUserProfile(): Promise<any> {
    if (!this.token) {
      throw new Error('No token available');
    }
    
    try {
      const userInfoEndpoint = `${this.authServerUrl}/realms/${this.realm}/protocol/openid-connect/userinfo`;
      
      const response = await fetch(userInfoEndpoint, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (response.ok) {
        this.profile = await response.json();
        return this.profile;
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      throw error;
    }
  }
}

const keycloak = new KeycloakAdapter({
  url: keycloakUrl,
  realm: realm,
  clientId: clientId,
});

console.log('📍 Auth endpoints:');
console.log('  Login:', `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth`);
console.log('  Token:', `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`);
console.log('  Logout:', `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`);

export default keycloak;